import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";

import { getModuleKindDisplayName } from "src/adminPanel/modulesV2/utils";
import { moduleKeys, useCreateModule } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { Combobox } from "src/base-components/Combobox";
import { Divider } from "src/base-components/Divider";
import { DesiredSpec, ModuleKind } from "src/clients/flow-api";
import { Callout } from "src/design-system/Callout";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";

type Workspace = {
  id: string;
  name: string;
  slug: string;
  organization_id: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  moduleKind: ModuleKind;
  workspaces: Workspace[];
  existingWorkspaceIds: Set<string>;
  orgMap: Map<string, string>;
  agentWorkspaceIds: Set<string>;
};

type WorkspaceOption = {
  key: string;
  value: React.ReactNode;
  searchText: string;
  workspace: Workspace;
  sectionKey: string;
};

const workspaceSections = [{ key: "workspaces", displayText: "Workspaces" }];

const renderWorkspaceColumnHeaders = () => (
  <>
    <div className="flex items-center gap-2 px-4 py-2 font-inter-semibold-12px">
      <span className="w-52">Workspace</span>
      <span className="w-40">Slug</span>
      <span className="min-w-0 flex-auto">Organization</span>
    </div>
    <Divider variant="dark" />
  </>
);

export const EnableModuleModal: React.FC<Props> = ({
  isOpen,
  onClose,
  afterLeave,
  moduleKind,
  workspaces,
  existingWorkspaceIds,
  orgMap,
  agentWorkspaceIds,
}) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");
  const queryClient = useQueryClient();
  const createModule = useCreateModule();

  const displayName = getModuleKindDisplayName(moduleKind);

  const availableWorkspaces = workspaces.filter(
    (ws) => !existingWorkspaceIds.has(ws.id),
  );

  const requiresAgentModule =
    moduleKind === ModuleKind.CASE_MANAGEMENT &&
    !!selectedWorkspaceId &&
    !agentWorkspaceIds.has(selectedWorkspaceId);

  const workspaceOptions: WorkspaceOption[] = availableWorkspaces.map(
    ({ id, name, slug, organization_id }) => {
      const orgName = orgMap.get(organization_id) ?? organization_id;

      return {
        key: id,
        sectionKey: "workspaces",
        value: (
          <div className="flex items-center gap-2 font-inter-normal-12px">
            <span className="w-52 text-gray-800">{name}</span>
            <span className="w-40 text-gray-500">{slug}</span>
            <span className="min-w-0 flex-auto text-gray-400">{orgName}</span>
          </div>
        ),
        searchText: `${name} ${slug} ${id} ${organization_id} ${orgName}`,
        workspace: { id, name, slug, organization_id },
      };
    },
  );

  const handleCreate = async () => {
    if (!selectedWorkspaceId) return;

    const selectedWorkspace = availableWorkspaces.find(
      (ws) => ws.id === selectedWorkspaceId,
    );
    if (!selectedWorkspace) return;

    try {
      await createModule.mutateAsync({
        data: {
          type: "modules",
          attributes: {
            desired_spec: { kind: moduleKind } as DesiredSpec,
          },
          relationships: {
            workspace: {
              data: { type: "workspaces", id: selectedWorkspaceId },
            },
          },
        },
      });

      await queryClient.invalidateQueries({ queryKey: moduleKeys.all() });

      toastActions.success({
        title: "Module enabled successfully",
        description: `${displayName} module has been enabled for ${selectedWorkspace.name}`,
      });

      handleClose();
    } catch (error) {
      logger.error("Failed to enable module", error);
      toastActions.failure({
        title: "Failed to enable module",
        description: "Please try again or start a thread in #team-platform",
      });
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedWorkspaceId("");
  };

  const selectedWorkspace = availableWorkspaces.find(
    (ws) => ws.id === selectedWorkspaceId,
  );

  return (
    <Modal
      afterLeave={afterLeave}
      open={isOpen}
      size="md"
      onClose={handleClose}
    >
      <Modal.Header>Enable {displayName} Module</Modal.Header>
      <Modal.Content>
        <div className="mb-20 space-y-4">
          <p className="font-inter-normal-13px text-gray-600">
            Select a workspace to enable the{" "}
            <span className="font-inter-semibold-13px">
              {displayName.toLowerCase()}
            </span>{" "}
            module for. Only workspaces that don&apos;t already have this module
            are shown.
          </p>

          <div className="space-y-2">
            <label className="block font-inter-semibold-13px text-gray-700">
              Select Workspace
            </label>
            <Combobox
              dropdownPlaceholder={
                <div className="px-4 py-8 text-center font-inter-normal-13px text-gray-500">
                  {availableWorkspaces.length === 0
                    ? "All workspaces already have this module enabled"
                    : "No workspaces match your search"}
                </div>
              }
              options={workspaceOptions}
              placeholder="Search by workspace name, slug, ID, or organization..."
              sectionRenderer={renderWorkspaceColumnHeaders}
              sections={workspaceSections}
              value={selectedWorkspaceId}
              onChange={(value) => setSelectedWorkspaceId(value || "")}
            />
          </div>

          {selectedWorkspace && (
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-4 font-inter-semibold-13px text-gray-900">
                Selected Workspace Details
              </h3>

              <div className="grid grid-cols-1 gap-4 font-inter-normal-13px">
                <div className="space-y-3">
                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Organization
                    </div>
                    <a
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      href={`/admin-panel/organizations?search=${selectedWorkspace.organization_id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {orgMap.get(selectedWorkspace.organization_id) ??
                        selectedWorkspace.organization_id}
                    </a>
                    <div className="font-code-12 text-gray-500">
                      ID: {selectedWorkspace.organization_id}
                    </div>
                  </div>

                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Workspace
                    </div>
                    <a
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      href={`/admin-panel/organizations?search=${selectedWorkspace.id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {selectedWorkspace.name}
                    </a>
                    <div className="font-code-12 text-gray-500">
                      Slug: {selectedWorkspace.slug}
                    </div>
                    <div className="font-code-12 text-gray-500">
                      ID: {selectedWorkspace.id}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {requiresAgentModule && (
            <Callout variant="warning">
              Agent module has to be enabled in order to create case management
              module for this workspace.
            </Callout>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            disabled={
              !selectedWorkspaceId ||
              createModule.isPending ||
              requiresAgentModule
            }
            iconLeft={faPlus}
            loading={createModule.isPending}
            variant="primary"
            onClick={handleCreate}
          >
            {createModule.isPending ? "Enabling..." : "Enable Module"}
          </Button>
        }
        secondaryButton="Cancel"
      />
    </Modal>
  );
};
